import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, type Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Loading from "@/components/Loading";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  type AdministrativeArea,
  type AdministrativeAreasModalOptions,
  type AdministrativeAreaType,
  type EntityId,
} from "../../types";

interface FormProps {
  administrativeAreas?: AdministrativeArea[];
  administrativeAreaTypes?: AdministrativeAreaType[];
  initialValues?: Partial<AdministrativeAreaFormValues>;
  isFetching?: boolean;
  modalOptions: AdministrativeAreasModalOptions;
  onSubmit: (values: AdministrativeAreaFormValues) => void;
}

interface SelectOption {
  label: string;
  selectValue: string;
  value: EntityId | null;
}

export const ADMINISTRATIVE_AREA_FORM_ID = "administrative-area-form";

const NO_PARENT_SELECT_VALUE = "__no_parent__";
const EMPTY_VALUE = "-";

const entityIdSchema = z.union([z.string(), z.number()]);

export const createAdministrativeAreaSchema = (requiredMessage: string) =>
  z.object({
    active: z.boolean().default(true),
    country: entityIdSchema.nullish(),
    id: entityIdSchema.nullish(),
    name: z.preprocess(
      value => value ?? "",
      z.string().trim().min(1, { message: requiredMessage })
    ),
    parent: entityIdSchema.nullable().default(null),
    type: entityIdSchema
      .nullable()
      .default(null)
      .refine(value => value !== null && value !== "", { message: requiredMessage }),
  });

export type AdministrativeAreaFormValues = z.infer<
  ReturnType<typeof createAdministrativeAreaSchema>
>;

const DEFAULT_FORM_VALUES: AdministrativeAreaFormValues = {
  active: true,
  name: "",
  parent: null,
  type: null,
};

const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return "";
};

const getSelectValue = (value: EntityId | null | undefined) =>
  value === undefined || value === null ? NO_PARENT_SELECT_VALUE : String(value);

const getLabel = (entity: { name?: string | null }) => entity.name?.trim() || EMPTY_VALUE;

const getLevel = (areaType?: AdministrativeAreaType) => areaType?.admin_level ?? 0;

const createTypeLookup = (administrativeAreaTypes: AdministrativeAreaType[]) =>
  new Map(
    administrativeAreaTypes.flatMap(areaType =>
      areaType.id === undefined || areaType.id === null
        ? []
        : [[String(areaType.id), areaType] as const]
    )
  );

export const normalizeInitialValues = (
  initialValues?: Partial<AdministrativeAreaFormValues>
): AdministrativeAreaFormValues => ({
  ...DEFAULT_FORM_VALUES,
  ...initialValues,
  active: initialValues?.active ?? DEFAULT_FORM_VALUES.active,
  name: initialValues?.name ?? DEFAULT_FORM_VALUES.name,
  parent: initialValues?.parent ?? DEFAULT_FORM_VALUES.parent,
  type: initialValues?.type ?? DEFAULT_FORM_VALUES.type,
});

export const createTypeOptions = (
  administrativeAreaTypes: AdministrativeAreaType[]
): SelectOption[] =>
  [...administrativeAreaTypes]
    .sort((a, b) => getLevel(a) - getLevel(b) || getLabel(a).localeCompare(getLabel(b)))
    .map(areaType => ({
      label: getLabel(areaType),
      selectValue: getSelectValue(areaType.id),
      value: areaType.id ?? null,
    }));

// Only areas typed above the selected type in the area-type tree can act as its parent.
export const createParentAreaOptions = (
  administrativeAreas: AdministrativeArea[],
  administrativeAreaTypes: AdministrativeAreaType[],
  selectedTypeId: EntityId | null | undefined,
  currentAreaId: EntityId | null | undefined,
  noParentLabel: string
): SelectOption[] => {
  const noParentOption: SelectOption = {
    label: noParentLabel,
    selectValue: NO_PARENT_SELECT_VALUE,
    value: null,
  };

  const typeLookup = createTypeLookup(administrativeAreaTypes);
  const selectedType =
    selectedTypeId === undefined || selectedTypeId === null
      ? undefined
      : typeLookup.get(String(selectedTypeId));
  if (!selectedType) {
    return [noParentOption];
  }

  const selectedLevel = getLevel(selectedType);
  const currentAreaKey =
    currentAreaId === undefined || currentAreaId === null ? undefined : String(currentAreaId);

  const parentAreas = administrativeAreas
    .flatMap(area => {
      const areaType = typeLookup.get(String(area.type));

      if (!areaType || getLevel(areaType) >= selectedLevel) {
        return [];
      }

      return String(area.id) === currentAreaKey ? [] : [{ area, areaType }];
    })
    .sort(
      (a, b) =>
        getLevel(a.areaType) - getLevel(b.areaType) ||
        getLabel(a.area).localeCompare(getLabel(b.area))
    )
    .map(({ area, areaType }) => ({
      label: `${getLabel(area)} (${getLabel(areaType)})`,
      selectValue: getSelectValue(area.id),
      value: area.id ?? null,
    }));

  return [noParentOption, ...parentAreas];
};

export const Form = ({
  administrativeAreas = [],
  administrativeAreaTypes = [],
  initialValues,
  isFetching = false,
  modalOptions,
  onSubmit,
}: FormProps) => {
  const { t } = useTranslation();
  const isViewMode = modalOptions.type === "view";
  const schema = useMemo(
    () => createAdministrativeAreaSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );
  const defaultValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);
  const typeOptions = useMemo(
    () => createTypeOptions(administrativeAreaTypes),
    [administrativeAreaTypes]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<AdministrativeAreaFormValues>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<AdministrativeAreaFormValues>,
  });

  const selectedTypeId = useWatch({ control, name: "type" });
  const parentOptions = useMemo(
    () =>
      createParentAreaOptions(
        administrativeAreas,
        administrativeAreaTypes,
        selectedTypeId,
        defaultValues.id,
        t("administrativeAreaTypes.noParent")
      ),
    [administrativeAreas, administrativeAreaTypes, defaultValues.id, selectedTypeId, t]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, modalOptions.id, modalOptions.type, reset]);

  const getFieldError = (fieldName: FieldPath<AdministrativeAreaFormValues>) =>
    toErrorMessage(errors[fieldName]?.message);

  if (isFetching) {
    return <Loading isVisible />;
  }

  return (
    <form
      id={ADMINISTRATIVE_AREA_FORM_ID}
      className="flex flex-col gap-5"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="name"
        render={({ field }) => {
          const error = getFieldError("name");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-name">
                {t("configuration.common.name")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                {...field}
                id="input-name"
                value={field.value ?? ""}
                disabled={isViewMode}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-name-error" : undefined}
              />
              <FieldError id="input-name-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="type"
        render={({ field }) => {
          const error = getFieldError("type");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-type">
                {t("administrativeAreas.form.type")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <FieldDescription id="input-type-description">
                {t("administrativeAreas.form.typeHelper")}
              </FieldDescription>
              <Select
                value={field.value === null || field.value === undefined ? "" : String(field.value)}
                disabled={isViewMode}
                onValueChange={value => {
                  const selectedOption = typeOptions.find(option => option.selectValue === value);
                  field.onChange(selectedOption?.value ?? null);
                  setValue("parent", null);
                }}
              >
                <SelectTrigger
                  id="input-type"
                  className="w-full"
                  aria-invalid={Boolean(error)}
                  aria-describedby={
                    error ? "input-type-description input-type-error" : "input-type-description"
                  }
                >
                  <SelectValue placeholder={t("common.select")} />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map(option => (
                    <SelectItem key={option.selectValue} value={option.selectValue}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError id="input-type-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="parent"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="input-parent">{t("administrativeAreas.form.parent")}</FieldLabel>
            <FieldDescription id="input-parent-description">
              {t("administrativeAreas.form.parentHelper")}
            </FieldDescription>
            <Select
              value={getSelectValue(field.value)}
              disabled={isViewMode || !selectedTypeId}
              onValueChange={value => {
                const selectedOption = parentOptions.find(option => option.selectValue === value);
                field.onChange(selectedOption?.value ?? null);
              }}
            >
              <SelectTrigger
                id="input-parent"
                className="w-full"
                aria-describedby="input-parent-description"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {parentOptions.map(option => (
                  <SelectItem key={option.selectValue} value={option.selectValue}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
      />

      <Controller
        control={control}
        name="active"
        render={({ field }) => (
          <Field orientation="horizontal">
            <Checkbox
              id="active"
              checked={Boolean(field.value)}
              disabled={isViewMode}
              onCheckedChange={checked => field.onChange(checked === true)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
            <FieldLabel htmlFor="active">{t("configuration.common.active")}</FieldLabel>
          </Field>
        )}
      />
    </form>
  );
};
